import { supabase } from './supabase';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Mock Data for Demo Mode
const DEMO_CLIENTS = [
  { id: 'c1', name: 'Maria Garcia', email: 'maria@example.com', currentCount: 4, threshold: 5, last_visit: new Date().toISOString(), walletLink: '#', walletObjectId: 'demo-c1' },
  { id: 'c2', name: 'Alex Johnson', email: 'alex@example.com', currentCount: 2, threshold: 5, last_visit: new Date(Date.now() - 86400000).toISOString(), walletLink: '#', walletObjectId: 'demo-c2' },
  { id: 'c3', name: 'Sarah Miller', email: 'sarah@example.com', currentCount: 5, threshold: 5, last_visit: new Date(Date.now() - 172800000).toISOString(), walletLink: '#', walletObjectId: 'demo-c3' },
  { id: 'c4', name: 'David Chen', email: 'david@example.com', currentCount: 1, threshold: 5, last_visit: new Date(Date.now() - 259200000).toISOString(), walletLink: '#', walletObjectId: 'demo-c4' }
];

const DEMO_ACTIVITY = [
  { id: 'a1', type: 'visit', client_name: 'Maria Garcia', created_at: new Date().toISOString() },
  { id: 'a2', type: 'reward', client_name: 'Sarah Miller', created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: 'a3', type: 'new_client', client_name: 'David Chen', created_at: new Date(Date.now() - 86400000).toISOString() }
];

const DEMO_STATS = {
  activeClients: 45,
  clientTrend: 12,
  totalVisits: 128,
  visitsTrend: 8,
  rewardsGiven: 15,
  conversionRate: 35,
  conversionTrend: 5
};

const DEMO_NOTIF_STATUS = {
  canSend: true,
  clientCount: 4,
  lastSent: new Date(Date.now() - 86400000 * 5).toISOString(),
  nextAvailable: null
};

async function request(method, path, body) {
  // --- DEMO MODE INTERCEPTION ---
  if (localStorage.getItem('wafiyo_demo_mode') === 'true') {
    console.log(`[Demo Mode] Intercepting ${method} ${path}`);

    // Simulate slight network delay
    await new Promise(resolve => setTimeout(resolve, 300));

    // GET Requests
    if (method === 'GET') {
      if (path.startsWith('/clients')) return DEMO_CLIENTS;
      if (path.startsWith('/activity')) return DEMO_ACTIVITY;
      if (path.startsWith('/stats')) return DEMO_STATS;
      if (path.startsWith('/analytics')) {
        return {
          metrics: {
            weeklyVisits: 128,
            visitsTrend: 15,
            retentionRate: 68,
            retentionTrend: 5,
            stampsThisWeek: 450,
            stampsTrend: 12,
            conversionRate: 35
          },
          dailyVisits: [
            { day: 'Lun', visits: 12 }, { day: 'Mar', visits: 18 }, { day: 'Mie', visits: 15 },
            { day: 'Jue', visits: 22 }, { day: 'Vie', visits: 35 }, { day: 'Sab', visits: 42 }, { day: 'Dom', visits: 28 }
          ],
          monthlyVisits: [
            { day: 'Sem 1', visits: 85 }, { day: 'Sem 2', visits: 92 }, { day: 'Sem 3', visits: 78 }, { day: 'Sem 4', visits: 110 }
          ],
          yearlyVisits: [
            { day: 'Ene', visits: 320 }, { day: 'Feb', visits: 280 }, { day: 'Mar', visits: 350 }, { day: 'Abr', visits: 310 }
          ],
          hourlyVisits: Array.from({ length: 24 }, (_, i) => ({ hour: `${i}:00`, visits: Math.floor(Math.random() * 10) + 2 })),
          retentionData: [{ name: 'Recurrentes', value: 68 }, { name: 'Nuevos', value: 32 }],
          topClients: [
            { name: 'Maria Garcia', visits: 12, stamps: 4, threshold: 5, lastVisit: new Date().toISOString() },
            { name: 'Alex Johnson', visits: 8, stamps: 2, threshold: 5, lastVisit: new Date(Date.now() - 86400000).toISOString() }
          ],
          insights: {
            bestDay: 'Sábado',
            bestDayCount: 42,
            peakHourStart: 18,
            peakHourEnd: 20,
            peakHourCount: 15,
            almostReward: 3
          }
        };
      }
      if (path.startsWith('/notifications/status')) return DEMO_NOTIF_STATUS;
      if (path.startsWith('/design')) return {
        bg_color: '#1A7A4A', text_color: '#FFFFFF', header_mode: 'gradient',
        logo_url: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&q=80&w=200&h=200'
      };
      return {};
    }

    // POST/PUT/DELETE Mutations (Fake Success)
    if (path.startsWith('/design')) return { success: true };
    if (path.startsWith('/scan-qr')) return {
      success: true,
      message: 'Visit recorded! +1 stamp',
      clientName: 'Maria Garcia',
      newCount: 4,
      threshold: 5,
      rewardEarned: false
    };
    if (path.startsWith('/create-client')) {
      return {
        success: true,
        client: { id: 'c' + Date.now(), ...body, currentCount: 0 },
        card: {
          wallet_link: '#',
          wallet_object_id: 'demo-new-apple',
          qrCode: 'https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=demo-client'
        }
      };
    }
    if (path.startsWith('/notifications/send')) return { success: true };

    return { success: true };
  }
  // --- END DEMO MODE ---

  // Get the active session token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  // Ensure no double slashes by cleaning both parts and joining with a single one
  const cleanBase = BASE.replace(/\/+$/, '');
  const cleanPath = path.replace(/^\/+/, '');
  const url = `${cleanBase}/${cleanPath}`;

  try {
    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: body ? JSON.stringify(body) : undefined
    });

    const contentType = res.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await res.json();
    } else {
      const text = await res.text();
      console.error(`[API Error] Expected JSON but got ${contentType}. Body:`, text.substring(0, 500));
      throw new Error(`Server returned ${res.status}: ${res.statusText}. Check console for details.`);
    }

    if (!res.ok) {
      throw new Error(data?.error || `Request failed with status ${res.status}`);
    }

    return data;
  } catch (err) {
    console.error(`[API Request Failed] ${method} ${url}`, err);
    throw err;
  }
}

export const api = {
  createClientCard: (payload) => request('POST', '/create-client-card', payload),
  recordVisit: (clientId) => request('POST', '/scan-qr', { clientId }),
  scanQR: (clientId) => request('POST', '/scan-qr', { clientId }),
  removeVisit: (clientId) => request('POST', '/remove-visit', { clientId }),
  getClients: (businessId) => request('GET', `/clients?businessId=${businessId}`),
  getCardInfo: (clientId) => request('GET', `/card-info/${clientId}`),
  getBusinessQR: (businessId) => request('GET', `/business-qr/${businessId}`),
  getBusinessPublic: (businessId) => request('GET', `/business-qr/${businessId}`),
  sendContactMessage: (payload) => request('POST', '/contact', payload),
  getDesign: (businessId) => request('GET', `/design/${businessId}`),
  saveDesign: (businessId, config) => request('PUT', `/design/${businessId}`, config),
  redeemReward: (clientId) => request('POST', '/redeem-reward', { clientId }),
  getActivity: (businessId) => request('GET', `/activity?businessId=${businessId}`),
  activateSubscription: (businessId) => request('POST', '/api/activate-subscription', { businessId }),
  createCheckout: (businessId, productId, lang) => request('POST', '/api/create-checkout', { businessId, productId, lang }),
  getPortalSession: (businessId) => request('POST', '/api/polar/customer-portal', { businessId }),
  getDashboardStats: (businessId) => request('GET', `/stats?businessId=${businessId}`),
  getNotificationStatus: (businessId) => request('GET', `/notifications/status?businessId=${businessId}`),
  sendNotification: (businessId, message) => request('POST', '/notifications/send', { businessId, message }),
  getAnalytics: (businessId) => request('GET', `/analytics?businessId=${businessId}`),
  forgotPassword: (payload) => request('POST', '/api/auth/forgot-password', payload),
  register: async (payload) => {
    const cleanBase = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/+$/, '');
    const res = await fetch(`${cleanBase}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Registration failed');
    return data;
  },
  adminGetStats: () => request('GET', '/admin/stats'),
  adminGetBusinesses: () => request('GET', '/admin/businesses'),
  adminDeleteBusiness: (id) => request('DELETE', `/admin/businesses/${id}`),
  adminSendReminder: (id) => request('POST', `/admin/send-reminder/${id}`),
  adminGetVisitCard: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const cleanBase = BASE.replace(/\/+$/, '');
    const res = await fetch(`${cleanBase}/admin/visit-card`, {
      headers: { 'Authorization': token ? `Bearer ${token}` : '' }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(err.error || 'Failed to download visit card');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wafiyo-visit-card.pkpass';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
};
